// src/components/PrivateChat.js

import React, { useState, useEffect, useRef } from 'react'
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  serverTimestamp,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  setDoc
} from 'firebase/firestore'
import {
  ref,
  uploadBytesResumable,
  getDownloadURL
} from 'firebase/storage'
import { db, storage } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import {
  TextField,
  Button,
  IconButton,
  Stack,
  Box,
  Typography,
  LinearProgress,
  Snackbar
} from '@mui/material'
import PhotoCamera from '@mui/icons-material/PhotoCamera'

import ChatWindow from './ChatWindow'

// Çoklu dil
import { useTranslation } from 'react-i18next'

export default function PrivateChat({ selectedUser, userRole }) {
  const { t } = useTranslation() // i18n Hook

  const { currentUser } = useAuth()
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(null)
  const [previewURL, setPreviewURL] = useState(null)

  const uploadTaskRef = useRef(null)

  // Bildirimler (opsiyonel)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')

  // chatId
  const chatId = [currentUser.uid, selectedUser.uid].sort().join('_')

  // lastRead güncelle
  const updateLastRead = async (chatId) => {
    if (!currentUser) return
    const statusRef = doc(db, 'userChatStatus', `${currentUser.uid}_${chatId}`)
    await setDoc(statusRef, { lastRead: serverTimestamp() }, { merge: true })
  }

  // Mesajları dinle
  useEffect(() => {
    if (!chatId) return

    const msgRef = collection(db, 'privateMessages')
    const q = query(
      msgRef,
      where('chatId', '==', chatId),
      orderBy('createdAt', 'asc')
    )

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const msgs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }))
      setMessages(msgs)
      await updateLastRead(chatId)
    })

    return () => unsubscribe()
  }, [chatId, currentUser])

  // Mesaj düzenle
  const handleEditMessage = async (messageId, newText) => {
    try {
      const messageRef = doc(db, 'privateMessages', messageId)
      await updateDoc(messageRef, {
        text: newText,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error(t('privateChat.deleteError'), error)
    }
  }

  // Mesaj sil
  const handleDeleteMessage = async (messageId) => {
    const confirmDelete = window.confirm(t('privateChat.deleteConfirm'))
    if (!confirmDelete) return

    try {
      const messageRef = doc(db, 'privateMessages', messageId)
      await deleteDoc(messageRef)
    } catch (error) {
      console.error(t('privateChat.deleteError'), error)
    }
  }

  // Dosya seçildi
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      setSelectedFile(file)
      setUploadProgress(null)
      uploadTaskRef.current = null

      // Önizleme
      const tempUrl = URL.createObjectURL(file)
      setPreviewURL(tempUrl)
    }
  }

  // Özel mesaj gönder
  const handleSendPrivateMessage = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() && !selectedFile) return

    // Observer rolü
    if (userRole === 'observer') {
      alert(t('privateChat.observerCantSend'))
      return
    }

    // "user" sadece Moderatöre gönderebilir
    if (userRole === 'participant' && selectedUser.role !== 'moderator') {
      alert(t('privateChat.userOnlyToModerator'))
      return
    }

    try {
      setUploadProgress(0)
      let fileUrl = null
      let fileType = null

      if (selectedFile) {
        fileType = selectedFile.type
        const fileRef = ref(storage, `privateUploads/${Date.now()}_${selectedFile.name}`)
        const uploadTask = uploadBytesResumable(fileRef, selectedFile)
        uploadTaskRef.current = uploadTask

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            setUploadProgress(progress)
          },
          (error) => {
            console.error(t('privateChat.fileUploadError'), error)
            setUploadProgress(null)
            uploadTaskRef.current = null
          },
          async () => {
            fileUrl = await getDownloadURL(fileRef)
            setUploadProgress(null)
            uploadTaskRef.current = null

            // Firestore'a ekle
            await addDoc(collection(db, 'privateMessages'), {
              chatId,
              participants: [currentUser.uid, selectedUser.uid],
              text: newMessage,
              fileUrl,
              fileType,
              userEmail: currentUser.email,
              userNickname: currentUser.nickname || '',
              avatarUrl: currentUser.avatarUrl || '',
              createdAt: serverTimestamp(),
              userId: currentUser.uid
            })

            setNewMessage('')
            setSelectedFile(null)
            setPreviewURL(null)
          }
        )
      } else {
        // Sadece metin
        await addDoc(collection(db, 'privateMessages'), {
          chatId,
          participants: [currentUser.uid, selectedUser.uid],
          text: newMessage,
          fileUrl,
          fileType,
          userEmail: currentUser.email,
          userNickname: currentUser.nickname || '',
          avatarUrl: currentUser.avatarUrl || '',
          createdAt: serverTimestamp(),
          userId: currentUser.uid
        })

        setNewMessage('')
        setSelectedFile(null)
        setUploadProgress(null)
        setPreviewURL(null)
      }
    } catch (err) {
      console.error(t('privateChat.observerCantSend'), err)
      setUploadProgress(null)
    }
  }

  // Upload iptal
  const handleCancelUpload = () => {
    if (uploadTaskRef.current) {
      uploadTaskRef.current.cancel()
      uploadTaskRef.current = null
    }
    setSelectedFile(null)
    setPreviewURL(null)
    setUploadProgress(null)
    setNewMessage('')
  }

  // Snackbar kapatma
  const handleSnackbarClose = () => {
    setSnackbarOpen(false)
  }

  return (
    <Box>
      <ChatWindow
        messages={messages}
        currentUser={currentUser}
        onEditMessage={handleEditMessage}
        onDeleteMessage={handleDeleteMessage}
        userRole={userRole}
      />

      {/* Mesaj Gönderme Formu (Sadece moderatör veya user->moderator ise) */}
      {(userRole === 'moderator') && (
        <form onSubmit={handleSendPrivateMessage}>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <TextField
              label={t('privateChat.placeholder')}
              variant="outlined"
              fullWidth
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
            />
            <input
              accept="image/*,video/*,audio/*"
              style={{ display: 'none' }}
              id="icon-button-file-private"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="icon-button-file-private">
              <IconButton color="primary" aria-label="upload file" component="span">
                <PhotoCamera />
              </IconButton>
            </label>
            <Button type="submit" variant="contained">
              {t('privateChat.send')}
            </Button>
          </Stack>
        </form>
      )}

      {userRole === 'participant' && selectedUser.role === 'moderator' && (
        <form onSubmit={handleSendPrivateMessage}>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <TextField
              label={t('privateChat.placeholder')}
              variant="outlined"
              fullWidth
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
            />
            <input
              accept="image/*,video/*,audio/*"
              style={{ display: 'none' }}
              id="icon-button-file-private-user"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="icon-button-file-private-user">
              <IconButton color="primary" aria-label="upload file" component="span">
                <PhotoCamera />
              </IconButton>
            </label>
            <Button type="submit" variant="contained">
              {t('privateChat.send')}
            </Button>
          </Stack>
        </form>
      )}

      {selectedFile && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('privateChat.fileSelected')} {selectedFile.name}
            <Button variant="text" onClick={handleCancelUpload} sx={{ ml: 1 }}>
              {t('privateChat.cancel')}
            </Button>
          </Typography>
        </Box>
      )}

      {previewURL && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">
            {t('privateChat.preview')}
          </Typography>
          {selectedFile?.type.startsWith('image/') && (
            <img
              src={previewURL}
              alt="preview-img"
              style={{
                maxWidth: '100%',
                borderRadius: 8,
                marginTop: 8
              }}
            />
          )}
          {selectedFile?.type.startsWith('video/') && (
            <video
              src={previewURL}
              controls
              style={{
                maxWidth: '100%',
                maxHeight: '400px',
                borderRadius: 8,
                marginTop: 8,
                objectFit: 'contain'
              }}
            />
          )}
          {selectedFile?.type.startsWith('audio/') && (
            <audio
              src={previewURL}
              controls
              style={{ width: '100%', marginTop: 8 }}
            />
          )}
        </Box>
      )}

      {uploadProgress !== null && uploadProgress < 100 && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2" align="center" sx={{ mt: 1 }}>
            {t('privateChat.uploading', { progress: Math.round(uploadProgress) })}
          </Typography>

          <Button
            variant="outlined"
            color="error"
            onClick={handleCancelUpload}
            sx={{ mt: 1, display: 'block', mx: 'auto' }}
          >
            {t('privateChat.cancelUpload')}
          </Button>
        </Box>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        action={
          <Button color="secondary" size="small" onClick={handleSnackbarClose}>
            {t('privateChat.cancel')}
          </Button>
        }
      />
    </Box>
  )
}